/// <amd-module name="Repos/PullRequestActions/Components/SharePullRequestPanel" />
import * as React from "react";
import * as Resources from "Repos/PullRequestActions/Resources";
import { IReadonlyObservableValue, ObservableArray, ObservableValue } from "VSS/Core/Observable";
import { PeoplePickerProvider } from "VSS/Features/Identities/IdentityPickerProvider";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { IButtonProps } from "VSSUI/Button";
import { FormItem } from "VSSUI/FormItem";
import { IdentityPicker, IIdentity } from "VSSUI/IdentityPicker";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { Observer } from "VSSUI/Observer";
import { Panel } from "VSSUI/Panel";
import { Spinner } from "VSSUI/Spinner";
import { TextField } from "VSSUI/TextField";

export interface ISharePullRequestPanelResult {
    identityIds: string[];
    message: string;
}

export interface ISharePullRequestPanelProps {
    errorMessage: IReadonlyObservableValue<string>;
    initialReviewers: IIdentity[];
    onClose: (result: ISharePullRequestPanelResult | undefined) => void;
    peoplePickerProvider: PeoplePickerProvider;
    showOverlay: IReadonlyObservableValue<boolean>;
    materializeIdentity: (identity: IIdentity) => Promise<IIdentity>;
}

export class SharePullRequestPanel extends VssComponent<ISharePullRequestPanelProps> {
    private readonly noteTextFieldValue = new ObservableValue<string>("");
    private readonly selectedIdentities = new ObservableArray<IIdentity>([]);
    private readonly fieldsValid = new ObservableValue(true);

    private readonly materializingIdentities = new ObservableArray<IIdentity>([]);
    private readonly failedMaterializeIdentities = new ObservableArray<IIdentity>([]);

    constructor(props: ISharePullRequestPanelProps, context: IVssComponentContext) {
        super(props, context);

        this.selectedIdentities = new ObservableArray<IIdentity>(this.props.initialReviewers);

        this.updateValid();
    }

    public render() {
        const { errorMessage, peoplePickerProvider, showOverlay } = this.props;

        return (
            <Observer
                errorMessage={errorMessage}
                showOverlay={showOverlay}
                valid={this.fieldsValid}
                materializingIdentities={this.materializingIdentities}
                failedMaterializeIdentities={this.failedMaterializeIdentities}
                selectedIdentities={this.selectedIdentities}
            >
                {(props: {
                    errorMessage?: string;
                    showOverlay: boolean;
                    valid: boolean;
                    materializingIdentities: IIdentity[];
                    failedMaterializeIdentities: IIdentity[];
                }) => {
                    const footerButtons: IButtonProps[] = [
                        {
                            id: "cancel",
                            onClick: () => this.onDismiss(),
                            text: Resources.Cancel
                        },
                        {
                            disabled: !props.valid,
                            id: "send",
                            onClick: () => this.onSend(),
                            primary: true,
                            text: Resources.Send
                        }
                    ];

                    return (
                        <Panel
                            description={Resources.SharePullRequestBody}
                            footerButtonProps={footerButtons}
                            onDismiss={this.onDismiss}
                            overlayContent={props.showOverlay ? <Spinner /> : undefined}
                            titleProps={{ text: Resources.SharePullRequest }}
                        >
                            <div className="flex-column flex-grow rhythm-vertical-16">
                                {props.errorMessage && <MessageCard severity={MessageCardSeverity.Error}>{props.errorMessage}</MessageCard>}
                                {props.failedMaterializeIdentities.length > 0 && (
                                    <MessageCard severity={MessageCardSeverity.Error}>
                                        {
                                            <div>
                                                <span>{Resources.SharePullRequestFailedMaterialize}</span>
                                                {props.failedMaterializeIdentities.map(identity => (
                                                    <p key={identity.entityId}>{identity.displayName}</p>
                                                ))}
                                            </div>
                                        }
                                    </MessageCard>
                                )}

                                {props.materializingIdentities.length > 0 && (
                                    <MessageCard severity={MessageCardSeverity.Info}>
                                        {
                                            <div>
                                                <span>{Resources.SharePullRequestMaterializing}</span>
                                                {props.materializingIdentities.map(identity => (
                                                    <p key={identity.entityId}>{identity.displayName}</p>
                                                ))}
                                            </div>
                                        }
                                    </MessageCard>
                                )}

                                <FormItem label={Resources.To}>
                                    <IdentityPicker
                                        onIdentitiesRemoved={this.onIdentitiesRemoved}
                                        onIdentityAdded={this.onIdentityAdded}
                                        onIdentityRemoved={this.onIdentityRemoved}
                                        pickerProvider={peoplePickerProvider}
                                        selectedIdentities={this.selectedIdentities.value.concat(this.materializingIdentities.value)}
                                    />
                                </FormItem>

                                <FormItem label={Resources.NoteOptional}>
                                    <TextField
                                        autoAdjustHeight={true}
                                        multiline={true}
                                        onChange={this.onNoteChanged}
                                        value={this.noteTextFieldValue}
                                    />
                                </FormItem>
                            </div>
                        </Panel>
                    );
                }}
            </Observer>
        );
    }

    private updateValid(): void {
        const hasIdenties: boolean = this.selectedIdentities.length > 0;
        const isMaterializing: boolean = this.materializingIdentities.length > 0;
        //since identities that could not be materialized are removed from the list, we do not need to check failedMaterializeIdentities here
        const valid: boolean = hasIdenties && !isMaterializing;

        if (this.fieldsValid.value !== valid) {
            this.fieldsValid.value = valid;
        }
    }

    private onDismiss = () => {
        this.props.onClose(undefined);
    };

    private onSend = () => {
        this.props.onClose({
            identityIds: this.selectedIdentities.value.map(x => x.localId!),
            message: this.noteTextFieldValue.value
        });
    };

    private onIdentitiesRemoved = (identities: IIdentity[]) => {
        const ids = new Set(identities.map(x => x.entityId));
        this.selectedIdentities.removeAll(x => ids.has(x.entityId));
        this.materializingIdentities.removeAll(x => ids.has(x.entityId));
        this.updateValid();
    };

    private onIdentityAdded = (identity: IIdentity) => {
        if (identity.localId) {
            //identity is already materialized
            this.selectedIdentities.push(identity);
            this.updateValid();
        } else if (identity.originId && this.materializingIdentities.value.findIndex(x => x.entityId == identity.entityId) < 0) {
            //identity is not materialized and we have not yet requested materialization
            this.materializingIdentities.push(identity);
            this.updateValid();

            //trying again, remove from the error list
            this.failedMaterializeIdentities.removeAll(x => x.entityId === identity.entityId);

            this.props
                .materializeIdentity(identity)
                .then((materializedIdentity: IIdentity) => {
                    //make sure the user hasn't removed the identity instead of waiting for materialization
                    const startLength = this.materializingIdentities.length;
                    this.materializingIdentities.removeAll(x => x.entityId === materializedIdentity.entityId);
                    const endLength = this.materializingIdentities.length;

                    if (startLength != endLength) {
                        this.selectedIdentities.push(materializedIdentity);
                        this.updateValid();
                    }
                })
                .catch((reason: any) => {
                    //make sure the user hasn't removed the identity instead of waiting for materialization
                    const startLength = this.materializingIdentities.length;
                    this.materializingIdentities.removeAll(x => x.entityId === identity.entityId);
                    const endLength = this.materializingIdentities.length;

                    if (startLength != endLength) {
                        this.failedMaterializeIdentities.push(identity);
                        this.updateValid();
                    }
                });
        }
    };

    private onIdentityRemoved = (identity: IIdentity) => {
        this.selectedIdentities.removeAll(x => x.entityId === identity.entityId);
        this.materializingIdentities.removeAll(x => x.entityId === identity.entityId);
        this.updateValid();
    };

    private onNoteChanged = (event: any, newValue: string) => {
        this.noteTextFieldValue.value = newValue;
    };
}
